import React, { useEffect, useState, useMemo } from "react";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Divider,
  Button,
} from "@mui/material";
import PercentageTextField from "../../shared/UIParts/InputFields/PercentageTextField";
import CurrencyTextField from "../../shared/UIParts/InputFields/CurrencyTextField";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { ProductType, AdjustedProductType, InvestmentPathDetail } from "../../interfaces/DTO/mislaka_xml_models";
import { fundService } from "../../services/fundService";
import { MimshakService } from "../../services/mimshakService";
import { FundDataBasic } from "../../interfaces/DTO/FundDataBasic";
import FundTable from "../GemelData/FundTable";
import { formatHandlerCode, formatCurrency } from "../../utils/namesFormater";
import { PolicyExposureChanges } from "../../hooks/feeLab/usePathDragChanges";
import { PreferenceDistributionData } from "../../interfaces/DTO/contactPreferences";
import MiniDoubleSlider from '../../shared/Sliders/MiniDoubleSlider';
import { PathFundSelection } from "../../services/MimshakProductUtil";

interface Props {
  mimshak: MimshakData;
  originalMimshak?: MimshakData;
  onFundSelected: (policyNumber: string, selectedFund: FundDataBasic, originalPathCode?: number) => void;
  onFundsSelected?: (policyNumber: string, selections: PathFundSelection[]) => void;
  onReset?: () => void;
  exposureChanges?: PolicyExposureChanges;
  preferenceData?: PreferenceDistributionData;
  onValidate?: (validate: () => string | null) => void;
  onFix?: (fix: () => void) => void;
  onCollect?: (collect: () => PathFundSelection[] | null) => void;
  /** splitting into several paths - a chosen fund is added as another path (instead of replacing the active one) */
  startInAddMode?: boolean;
}

interface FundWithDistance extends FundDataBasic {
  distance: number;
}

interface PathInfo {
  pathCode: number;
  pathName: string;
  amount: number;
  depositPercentage?: number;
  fund: FundDataBasic | null;
  selectedFund: FundDataBasic | null;
  isNew?: boolean;
}

const SuggestedPathSelection: React.FC<Props> = ({ mimshak, originalMimshak, onFundSelected, onFundsSelected, onReset, exposureChanges, preferenceData, onValidate, onFix, onCollect, startInAddMode = false }) => {
  const [selectedFundId, setSelectedFundId] = useState<number | null>(null);
  const [highlightedFundId, setHighlightedFundId] = useState<number | null>(null);
  const [performanceRatingRange, setPerformanceRatingRange] = useState({ min: 60, max: 100 });
  const [includeSectoral, setIncludeSectoral] = useState(false);

  // Multiple paths state
  const [paths, setPaths] = useState<PathInfo[]>([]);
  const [activePathCode, setActivePathCode] = useState<number | null>(null);
  const [editingDepositPathCode, setEditingDepositPathCode] = useState<number | null>(null);
  const [editingAmountPathCode, setEditingAmountPathCode] = useState<number | null>(null);
  const [editingPctPathCode, setEditingPctPathCode] = useState<number | null>(null);

  useEffect(() => {
    if (!onValidate) return;
    onValidate(() => {
      const visible = paths.filter(p => !p.fund || String(p.fund.managing_corporation_legal_id) === String(mimshak.handler_code));
      if (visible.length === 0) return null;
      const expectedTotal = mimshak.funds_sum;
      const actualTotal = visible.reduce((s, p) => s + p.amount, 0);
      if (expectedTotal > 0 && Math.round(actualTotal) !== Math.round(expectedTotal)) {
       
        return `סכום המסלולים (${formatCurrency(actualTotal)}) אינו מסתכם ל 100% מסכום הפוליסה (${formatCurrency(expectedTotal)})`;
      }
      if (visible.length <= 1) return null;
      const totalPct = expectedTotal > 0 ? visible.reduce((s, p) => s + (p.amount / expectedTotal) * 100, 0) : 0;
      if (expectedTotal > 0 && Math.round(totalPct) !== 100) {
        return `חלוקת הסכומים מסתכמת ל-${totalPct.toFixed(1)}% במקום 100%`;
      }
      return null;
    });
  }, [onValidate, paths, mimshak.funds_sum, mimshak.handler_code]);

  useEffect(() => {
    if (!onFix) return;
    onFix(() => {
      const visible = paths.filter(p => !p.fund || String(p.fund.managing_corporation_legal_id) === String(mimshak.handler_code));
      if (visible.length === 0) return;
      const hidden = paths.filter(p => p.fund && String(p.fund.managing_corporation_legal_id) !== String(mimshak.handler_code));
      const totalAmount = mimshak.funds_sum || visible.reduce((s, p) => s + p.amount, 0);

      const applyAndSet = (fixedVisible: PathInfo[]) => setPaths([...hidden, ...fixedVisible]);

      // תיקון סכומים — single path: set directly; multiple: distribute proportionally
      let working = [...visible];

      if (totalAmount > 0) {
        const currentTotal = working.reduce((s, p) => s + p.amount, 0);
        if (Math.round(currentTotal) !== Math.round(totalAmount)) {
          if (working.length === 1) {
            working = [{ ...working[0], amount: totalAmount }];
          } else {
            const newPaths = working.filter(p => p.isNew);
            const origPaths = working.filter(p => !p.isNew);
            const newTotal = newPaths.reduce((s, p) => s + p.amount, 0);
            const remaining = totalAmount - newTotal;

            if (remaining >= 0 && origPaths.length > 0) {
              const origTotal = origPaths.reduce((s, p) => s + p.amount, 0);
              working = working.map(p => {
                if (p.isNew) return p;
                const share = origTotal > 0 ? p.amount / origTotal : 1 / origPaths.length;
                return { ...p, amount: Math.round(remaining * share) };
              });
              const fixedTotal = working.reduce((s, p) => s + p.amount, 0);
              const diff = totalAmount - fixedTotal;
              if (diff !== 0) {
                const lastOrig = [...working].reverse().find(p => !p.isNew);
                if (lastOrig) working = working.map(p => p.pathCode === lastOrig.pathCode ? { ...p, amount: p.amount + diff } : p);
              }
            } else if (origPaths.length > 0) {
              const randomOrig = origPaths[Math.floor(Math.random() * origPaths.length)];
              working = working.map(p => p.isNew ? p : { ...p, amount: p.pathCode === randomOrig.pathCode ? Math.max(0, totalAmount - newTotal) : 0 });
            } else {
              const randomNew = working[Math.floor(Math.random() * working.length)];
              working = working.map((p, i) => i === working.indexOf(randomNew)
                ? { ...p, amount: totalAmount - working.filter((_, j) => j !== i).reduce((s, p2) => s + p2.amount, 0) }
                : p
              );
            }
          }
        }
      }

      // תיקון הפקדות — על אותו working array
      if (working.length > 1) {
        const hasDeposit = working.some(p => p.depositPercentage != null && p.depositPercentage > 0);
        if (hasDeposit) {
          const totalDeposit = working.reduce((s, p) => s + (p.depositPercentage ?? 0), 0);
          if (Math.round(totalDeposit) !== 100) {
            const newPaths = working.filter(p => p.isNew);
            const origPaths = working.filter(p => !p.isNew);
            const newDepositTotal = newPaths.reduce((s, p) => s + (p.depositPercentage ?? 0), 0);
            const remaining = 100 - newDepositTotal;

            if (remaining >= 0 && origPaths.length > 0) {
              const origDepositTotal = origPaths.reduce((s, p) => s + (p.depositPercentage ?? 0), 0);
              working = working.map(p => {
                if (p.isNew) return p;
                const share = origDepositTotal > 0 ? (p.depositPercentage ?? 0) / origDepositTotal : 1 / origPaths.length;
                return { ...p, depositPercentage: Math.round(remaining * share * 10) / 10 };
              });
              const fixedTotal = working.reduce((s, p) => s + (p.depositPercentage ?? 0), 0);
              const diff = Math.round((100 - fixedTotal) * 10) / 10;
              if (diff !== 0) {
                const lastOrig = [...working].reverse().find(p => !p.isNew);
                if (lastOrig) working = working.map(p => p.pathCode === lastOrig.pathCode ? { ...p, depositPercentage: (p.depositPercentage ?? 0) + diff } : p);
              }
            } else if (origPaths.length > 0) {
              const randomOrig = origPaths[Math.floor(Math.random() * origPaths.length)];
              working = working.map(p => p.isNew ? p : { ...p, depositPercentage: p.pathCode === randomOrig.pathCode ? Math.max(0, remaining) : 0 });
            } else {
              const randomNew = working[Math.floor(Math.random() * working.length)];
              working = working.map(p =>
                p.pathCode === randomNew.pathCode
                  ? { ...p, depositPercentage: 100 - working.filter(p2 => p2.pathCode !== p.pathCode).reduce((s, p2) => s + (p2.depositPercentage ?? 0), 0) }
                  : p
              );
            }
          }
        }
      }

      applyAndSet(working);
    });
  }, [onFix, paths, mimshak.funds_sum, mimshak.handler_code]);

  // Helper to convert indices to min/max percentages
  const getRange = (indices: number[] | undefined): { min: number; max: number } => {
    if (!indices || indices.length === 0) return { min: 0, max: 100 };
    const minIndex = Math.min(...indices);
    const maxIndex = Math.max(...indices);
    return {
      min: minIndex * 10,
      max: (maxIndex + 1) * 10,
    };
  };

  const [currencyExposureRange, setCurrencyExposureRange] = useState(() => getRange(preferenceData?.currencyExposure));
  const [foreignExposureRange, setForeignExposureRange] = useState(() => getRange(preferenceData?.foreignExposure));

  useEffect(() => {
    setCurrencyExposureRange(getRange(preferenceData?.currencyExposure));
    setForeignExposureRange(getRange(preferenceData?.foreignExposure));
  }, [preferenceData]);

  // Extract paths from mimshak data
  useEffect(() => {
    if (!mimshak.data?.products?.length) { setPaths([]); return; }

    let merged: InvestmentPathDetail[] = [];
    mimshak.data.products.forEach((product) => {
      product.policies_or_accounts?.forEach((policy) => {
        if (policy.policy_number === mimshak.policy_number && policy.investment_paths) {
          merged = MimshakService.mergeDuplicateInvestmentPathsByPathCode(policy.investment_paths);
        }
      });
    });


    if (merged.length > 0) {
      const hasDeposit = merged.some(p => p.deposit_percentage != null && p.deposit_percentage > 0);
      const pathsInfo: PathInfo[] = merged.map((path) => ({
        pathCode: path.path_code,
        pathName: path.path_name || `מסלול ${path.path_code}`,
        amount: path.accumulated_amount || 0,
        depositPercentage: hasDeposit ? (path.deposit_percentage ?? 0) : undefined,
        fund: fundService.getFundFromCacheById(path.path_code) ?? null,
        selectedFund: null as FundDataBasic | null,
      }));
      setPaths(pathsInfo);
      setActivePathCode(startInAddMode ? -1 : pathsInfo[0].pathCode);
    } else {
      setPaths([]);
      setActivePathCode(startInAddMode ? -1 : null);
    }
  }, [mimshak.data, mimshak.policy_number, startInAddMode]);

  // Calculate current stock exposure (for displaying as currentValue marker)
  const currentStockExposure = useMemo(() => {
    if (paths.length > 1 && activePathCode != null) {
      const activePath = paths.find(p => p.pathCode === activePathCode);
      const fund = activePath?.fund;
      return fund?.stock_market_percent ?? 50;
    }
    const currentFundId = mimshak.fund_ids?.[0];
    if (currentFundId) {
      const currentFund = fundService.getFundFromCacheById(currentFundId);
      return currentFund?.stock_market_percent ?? 50;
    }
    return 50;
  }, [mimshak.fund_ids, paths, activePathCode]);

  // Calculate target stock exposure (from drag changes, preference data, or current fund)
  const targetStockExposure = useMemo(() => {
    if (exposureChanges?.stock_market_percent) {
      return exposureChanges.stock_market_percent.new_value;
    }
    if (preferenceData?.stockExposure && preferenceData.stockExposure.length > 0) {
      const { min, max } = getRange(preferenceData.stockExposure);
      return (min + max) / 2;
    }
    return currentStockExposure;
  }, [exposureChanges, preferenceData, currentStockExposure]);

  // Initialize stock exposure range state (±10 from target)
  const [stockExposureRange, setStockExposureRange] = useState({
    min: Math.max(0, targetStockExposure - 10),
    max: Math.min(100, targetStockExposure + 10)
  });

  // Update stock exposure range when target changes
  useEffect(() => {
    setStockExposureRange({
      min: Math.max(0, targetStockExposure - 10),
      max: Math.min(100, targetStockExposure + 10)
    });
  }, [targetStockExposure]);

  // Set highlighted fund
  useEffect(() => {
    if (paths.length > 1 && activePathCode != null) {
      const activePath = paths.find(p => p.pathCode === activePathCode);
      setHighlightedFundId(activePath?.fund?.fund_id ?? null);
    } else {
      const currentFundId = mimshak.fund_ids?.[0];
      if (currentFundId) setHighlightedFundId(currentFundId);
    }
  }, [paths, activePathCode, mimshak.fund_ids]);

  const { currentHandlerFunds, otherHandlerFunds } = useMemo(() => {
    try {
      let effectiveProductType = mimshak.product_type;
      if (mimshak.product_type === ProductType.PURE_SAVINGS_POLICY && mimshak.adjusted_product_type != null) {
        if (mimshak.adjusted_product_type === AdjustedProductType.ACTIVE_MANAGERS_INSURANCE ||
            mimshak.adjusted_product_type === AdjustedProductType.INACTIVE_MANAGERS_INSURANCE) {
          effectiveProductType = ProductType.LIFE_INSURANCE_SAVINGS;
        } else {
          effectiveProductType = mimshak.adjusted_product_type;
        }
      }
      const allFunds = fundService.getFundsFromCacheByCriteria({
        productType: effectiveProductType,
        sub_specialization: null,
        handler_code: null,
        forGeneralPublic: !includeSectoral,
      });

      // Returns 0 = in range, 1 = near (within 10%), 2 = out for a single dimension
      const cellScore = (value: number | null | undefined, min: number, max: number): number => {
        const v = value ?? 0;
        const effectiveMax = max === 100 ? 120 : max;
        if (v >= min && v <= effectiveMax) return 0;
        if (v >= min - 10 && v <= effectiveMax + 10) return 1;
        return 2;
      };

      // Lower score = more "green". Stock is the primary dimension; currency/foreign only if active.
      const rangeScore = (fund: FundDataBasic): number => {
        const stock = cellScore(fund.stock_market_percent, stockExposureRange.min, stockExposureRange.max);
        const currency = (currencyExposureRange.min > 0 || currencyExposureRange.max < 100)
          ? cellScore(fund.foreign_currency_exposure_percent, currencyExposureRange.min, currencyExposureRange.max)
          : 0;
        const foreign = (foreignExposureRange.min > 0 || foreignExposureRange.max < 100)
          ? cellScore(fund.foreign_exposure_percent, foreignExposureRange.min, foreignExposureRange.max)
          : 0;
        return stock * 9 + currency * 3 + foreign;
      };

      const isInRange = (fund: FundDataBasic) => rangeScore(fund) === 0;

      const fundsInRange = allFunds.filter(isInRange);
      const fundsWithDistance = fundsInRange
        .map((fund) => ({ ...fund, distance: Math.abs((fund.stock_market_percent ?? 0) - targetStockExposure) }))
        .sort((a, b) => {
          const distanceDiff = a.distance - b.distance;
          if (distanceDiff !== 0) return distanceDiff;
          return (b.cumulative_12m_yield ?? -Infinity) - (a.cumulative_12m_yield ?? -Infinity);
        });

      const allCurrentHandlerFunds = allFunds
        .filter((fund) => String(fund.managing_corporation_legal_id) === String(mimshak.handler_code))
        .sort((a, b) => {
          const scoreDiff = rangeScore(a) - rangeScore(b);
          if (scoreDiff !== 0) return scoreDiff;
          const distanceDiff = Math.abs((a.stock_market_percent ?? 0) - targetStockExposure)
                             - Math.abs((b.stock_market_percent ?? 0) - targetStockExposure);
          if (distanceDiff !== 0) return distanceDiff;
          return (b.cumulative_12m_yield ?? -Infinity) - (a.cumulative_12m_yield ?? -Infinity);
        });

      return {
        currentHandlerFunds: allCurrentHandlerFunds,
        otherHandlerFunds: fundsWithDistance.filter(
          (fund) => String(fund.managing_corporation_legal_id) !== String(mimshak.handler_code)
        ),
      };
    } catch {
      return { currentHandlerFunds: [], otherHandlerFunds: [] };
    }
  }, [mimshak.adjusted_product_type, mimshak.product_type, mimshak.handler_code, targetStockExposure, stockExposureRange, currencyExposureRange, foreignExposureRange, includeSectoral]);

  useEffect(() => {
    if (!onCollect) return;
    onCollect(() => {
      if (paths.length === 0) return null;

      const originalPathCodes = new Set(
        (mimshak.data?.products ?? []).flatMap(product =>
          (product.policies_or_accounts ?? []).flatMap(policy =>
            policy.policy_number === mimshak.policy_number
              ? (policy.investment_paths ?? []).map(p => p.path_code)
              : []
          )
        )
      );

      const hasSelectedFund = paths.some(p => p.selectedFund != null);
      const hasRemovedPath = paths.length < originalPathCodes.size;
      const hasAddedPath = paths.some(p => p.isNew);
      const originalPaths = (mimshak.data?.products ?? []).flatMap(product =>
        (product.policies_or_accounts ?? []).flatMap(policy =>
          policy.policy_number === mimshak.policy_number ? (policy.investment_paths ?? []) : []
        )
      );
      const hasAmountChange = paths.some(p => {
        const orig = originalPaths.find(op => op.path_code === p.pathCode);
        if (!orig) return false;
        if (p.amount !== undefined && p.amount !== orig.accumulated_amount) return true;
        if (p.depositPercentage !== undefined && p.depositPercentage !== orig.deposit_percentage) return true;
        return false;
      });

      if (!hasSelectedFund && !hasRemovedPath && !hasAddedPath && !hasAmountChange) return null;

      const visibleCodes = new Set(
        paths
          .filter(p => !p.fund || String(p.fund.managing_corporation_legal_id) === String(mimshak.handler_code))
          .map(p => p.pathCode)
      );
      // כשיש מסלולים חדשים, מסלולים נסתרים (מיצרן אחר) לא נכללים — הם הוחלפו
      const pathsToCollect = hasAddedPath
        ? paths.filter(p => p.isNew || visibleCodes.has(p.pathCode))
        : paths;

      const selections: PathFundSelection[] = pathsToCollect
        .filter(p => p.selectedFund != null || p.fund != null)
        .map(p => ({
          fund: p.selectedFund ?? p.fund!,
          amount: p.amount,
          depositPercentage: p.depositPercentage,
        }));
      console.log('[onCollect] selections:', selections.map(s => ({ fund: s.fund.fund_id, fundHandler: s.fund.managing_corporation_legal_id, amount: s.amount, deposit: s.depositPercentage })));
      return selections.length > 0 ? selections : null;
    });
  }, [onCollect, paths]);

  // Paths whose cube is actually visible (same handler as current mimshak.handler_code)
  const visiblePaths = paths.filter(
    (path) => !path.fund || String(path.fund.managing_corporation_legal_id) === String(mimshak.handler_code)
  );

  const handleFundSelect = (fund: FundDataBasic) => {
    setSelectedFundId(fund.fund_id);
    if (activePathCode === -1) {
      // הוסף כמסלול חדש
      const newPathCode = fund.fund_id;
      const othersHaveDeposit = paths.some(p => p.depositPercentage != null && p.depositPercentage > 0);
      const isFirstPath = visiblePaths.length === 0 && paths.filter(p => p.isNew).length === 0;
      const totalAmount = mimshak.funds_sum || paths.reduce((s, p) => s + p.amount, 0);
      const newPath: PathInfo = {
        pathCode: newPathCode,
        pathName: fund.fund_name,
        amount: isFirstPath ? totalAmount : 0,
        depositPercentage: othersHaveDeposit ? 0 : undefined,
        fund,
        selectedFund: fund,
        isNew: true,
      };
      if (isFirstPath) {
        setPaths(prev => [...prev, newPath]);
      } else {
        // חלק את הסכום שווה רק בין המסלולים הגלויים + החדש (לא כולל מסלולים נסתרים מיצרן אחר)
        const visibleAndNew = [...visiblePaths, newPath];
        const n = visibleAndNew.length;
        const amountShare = totalAmount > 0 ? Math.floor(totalAmount / n) : 0;
        const amountRemainder = totalAmount - amountShare * n;
        const depositShare = othersHaveDeposit ? Math.floor(100 / n) : undefined;
        const depositRemainder = depositShare != null ? 100 - depositShare * n : 0;
        console.log('[handleFundSelect] paths:', paths.length, 'visiblePaths:', visiblePaths.length, 'n:', n, 'totalAmount:', totalAmount, 'amountShare:', amountShare);
        const visibleCodes = new Set(visibleAndNew.map(p => p.pathCode));
        setPaths([
          ...paths.filter(p => !visibleCodes.has(p.pathCode)), // מסלולים נסתרים — ללא שינוי
          ...visibleAndNew.map((p, i) => ({
            ...p,
            amount: amountShare + (i === 0 ? amountRemainder : 0),
            depositPercentage: depositShare != null ? depositShare + (i === 0 ? depositRemainder : 0) : undefined,
          })),
        ]);
        setEditingAmountPathCode(newPathCode);
      }
      setActivePathCode(newPathCode);
    } else if (visiblePaths.length > 1 && activePathCode != null) {
      // מסלולים מרובים גלויים — שמור רק ב-state, אל תקרא onFundSelected
      setPaths(prev => prev.map(p =>
        p.pathCode === activePathCode ? { ...p, selectedFund: fund } : p
      ));
    } else {
      // מסלול יחיד — שלח snapshot ישירות, SelectNewPathDrawer יאמת לפני קבלה
      if (onFundsSelected) {
        const existingAmount = visiblePaths[0]?.amount ?? mimshak.funds_sum ?? 0;
        onFundsSelected(mimshak.policy_number, [{
          fund,
          amount: existingAmount,
          depositPercentage: visiblePaths[0]?.depositPercentage,
        }]);
      } else {
        onFundSelected(mimshak.policy_number, fund);
      }
    }
  };

  // Filter other handler funds by performance rating range
  const filteredOtherHandlerFunds = useMemo(() => {
    return otherHandlerFunds.filter((fund) => {
      const percentile = fund.percentile_6m_yield;
      if (percentile == null || isNaN(Number(percentile))) {
        return performanceRatingRange.min === 0 && performanceRatingRange.max === 100;
      }
      const rating = Number(percentile);
      return rating >= performanceRatingRange.min && rating <= performanceRatingRange.max;
    });
  }, [otherHandlerFunds, performanceRatingRange]);

  const hasProcessedData = mimshak.data?.products?.length !== undefined;
  const noFunds = !currentHandlerFunds.length && !otherHandlerFunds.length;

  if (noFunds && !hasProcessedData) {
    return <CircularProgress size={20} />;
  }

  // splitting a policy without paths yet - the path tiles (with "הוסף מסלול") are shown from the start
  const hasMultiplePaths = paths.length > 0 || startInAddMode;

  return (
    <Paper elevation={1} sx={{ p: 1.5, bgcolor: '#fafafa', overflowX: "auto" }}>
      {exposureChanges?.stock_market_percent && (
        <Typography variant="body1" color="info.main" fontWeight="bold" gutterBottom>
          שינוי מבוקש: {exposureChanges.stock_market_percent.old_value.toFixed(1)}% ← {exposureChanges.stock_market_percent.new_value.toFixed(1)}%
        </Typography>
      )}

      {onReset && (
        <Box mb={1} display="flex" justifyContent="flex-end">
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<RestartAltIcon />}
            onClick={onReset}
          >
            החזר מסלולים למקורי
          </Button>
        </Box>
      )}

      {/* Multi-path selector */}
      {hasMultiplePaths && (
        <Box mb={2}>
          <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" mb={0.75}>
            בחר מסלול לעדכון
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {visiblePaths.map((path) => {
              const isActive = path.pathCode === activePathCode;
              const totalAmount = mimshak.funds_sum || paths.reduce((s, p) => s + p.amount, 0);
              const pct = totalAmount > 0 ? (path.amount / totalAmount) * 100 : null;
              return (
                <Box
                  key={path.pathCode}
                  onClick={() => setActivePathCode(path.pathCode)}
                  sx={{
                    border: isActive ? '2px solid #1976d2' : '1px solid #bbb',
                    borderRadius: 2,
                    px: 1.5,
                    py: 1,
                    cursor: 'pointer',
                    bgcolor: isActive ? '#e3f2fd' : '#fff',
                    minWidth: 90,
                    alignSelf: 'stretch',
                    position: 'relative',
                  }}
                >
                  {/* X מחיקה */}
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      const remaining = paths.filter(p => p.pathCode !== path.pathCode);
                      const removedAmount = path.amount;
                      const totalRemaining = remaining.reduce((s, p) => s + p.amount, 0);
                      const removedDeposit = path.depositPercentage ?? 0;
                      const totalRemainingDeposit = remaining.reduce((s, p) => s + (p.depositPercentage ?? 0), 0);
                      const redistributed = remaining.map(p => ({
                        ...p,
                        amount: totalRemaining > 0
                          ? p.amount + removedAmount * (p.amount / totalRemaining)
                          : p.amount + removedAmount / remaining.length,
                        depositPercentage: p.depositPercentage != null
                          ? totalRemainingDeposit > 0
                            ? p.depositPercentage + removedDeposit * (p.depositPercentage / totalRemainingDeposit)
                            : p.depositPercentage + removedDeposit / remaining.length
                          : undefined,
                      }));
                      setPaths(redistributed);
                      if (activePathCode === path.pathCode) setActivePathCode(redistributed[0]?.pathCode ?? null);
                    }}
                    sx={{
                      position: 'absolute',
                      top: -8,
                      left: -8,
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'error.main' },
                      zIndex: 1,
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 10, color: '#fff' }} />
                  </Box>

                  <Typography variant="body2" fontWeight={600} display="block">
                    {path.pathCode} - {path.pathName}
                  </Typography>

                  {/* סכום */}
                  {path.isNew && editingAmountPathCode === path.pathCode ? (
                    <>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {formatCurrency(path.amount)}
                      </Typography>
                      <Box onClick={(e) => e.stopPropagation()}>
                        <PercentageTextField
                          size="small"
                          value={pct ?? 0}
                          autoFocus
                          onChange={(val) => {
                            const newAmount = (mimshak.funds_sum * val) / 100;
                            setPaths(prev => prev.map(p =>
                              p.pathCode === path.pathCode ? { ...p, amount: newAmount } : p
                            ));
                          }}
                          onBlur={() => setEditingAmountPathCode(null)}
                        />
                      </Box>
                    </>
                  ) : (path.amount > 0 || editingAmountPathCode === path.pathCode || path.isNew) && (
                    <>
                      {editingAmountPathCode === path.pathCode ? (
                        <Box onClick={(e) => e.stopPropagation()} sx={{ mt: 0.5 }}>
                          <CurrencyTextField
                            size="small"
                            value={path.amount}
                            autoFocus
                            onChange={(val) => {
                              if (val != null) setPaths(prev => prev.map(p =>
                                p.pathCode === path.pathCode ? { ...p, amount: val } : p
                              ));
                            }}
                            onBlur={() => setEditingAmountPathCode(null)}
                          />
                        </Box>
                      ) : (
                        <Box
                          display="flex" alignItems="center" gap={0.25}
                          sx={{ '&:hover .edit-icon': { opacity: 1 } }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {formatCurrency(path.amount)}
                          </Typography>
                          <EditIcon className="edit-icon" onClick={(e) => { e.stopPropagation(); setEditingAmountPathCode(path.pathCode); }} sx={{ fontSize: 15, color: 'primary.main', opacity: 0, transition: 'opacity 0.15s', cursor: 'pointer' }} />
                        </Box>
                      )}
                      {pct != null && (
                        editingPctPathCode === path.pathCode ? (
                          <Box onClick={(e) => e.stopPropagation()}>
                            <PercentageTextField
                              size="small"
                              value={pct}
                              autoFocus
                              onChange={(val) => {
                                const newAmount = (mimshak.funds_sum * val) / 100;
                                setPaths(prev => prev.map(p =>
                                  p.pathCode === path.pathCode ? { ...p, amount: newAmount } : p
                                ));
                              }}
                              onBlur={() => setEditingPctPathCode(null)}
                            />
                          </Box>
                        ) : (
                          <Box
                            display="flex" alignItems="center" gap={0.25}
                            sx={{ '&:hover .edit-icon-pct': { opacity: 1 } }}
                          >
                            <Typography variant="body1" fontWeight="bold">
                              {pct.toFixed(1)}%
                            </Typography>
                            <EditIcon className="edit-icon-pct" onClick={(e) => { e.stopPropagation(); setEditingPctPathCode(path.pathCode); }} sx={{ fontSize: 15, color: 'primary.main', opacity: 0, transition: 'opacity 0.15s', cursor: 'pointer' }} />
                          </Box>
                        )
                      )}
                    </>
                  )}

                  {/* הפקדה + inline edit */}
                  {path.depositPercentage != null && (
                    editingDepositPathCode === path.pathCode ? (
                      <Box onClick={(e) => e.stopPropagation()} sx={{ mt: 0.5 }}>
                        <PercentageTextField
                          size="small"
                          value={path.depositPercentage}
                          autoFocus
                          onChange={(val) => {
                            setPaths(prev => prev.map(p =>
                              p.pathCode === path.pathCode ? { ...p, depositPercentage: val } : p
                            ));
                          }}
                          onBlur={() => setEditingDepositPathCode(null)}
                        />
                      </Box>
                    ) : (
                      <Box
                        display="flex" alignItems="center" gap={0.25}
                        sx={{ '&:hover .edit-icon': { opacity: 1 } }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          הפקדה: {path.depositPercentage.toFixed(1)}%
                        </Typography>
                        <EditIcon className="edit-icon" onClick={(e) => { e.stopPropagation(); setEditingDepositPathCode(path.pathCode); }} sx={{ fontSize: 15, color: 'primary.main', opacity: 0, transition: 'opacity 0.15s', cursor: 'pointer' }} />
                      </Box>
                    )
                  )}

                  {path.selectedFund && (
                    <Typography variant="body2" display="block" sx={{ mt: 0.5, px: 0.75, py: 0.25, bgcolor: '#fffde7', border: '1.5px dashed red', borderRadius: 1 }}>
                      ← {path.selectedFund.fund_id} {path.selectedFund.fund_name}
                    </Typography>
                  )}
                </Box>
              );
            })}
            {/* הוסף מסלול */}
            <Box
              onClick={() => setActivePathCode(-1)}
              sx={{
                border: activePathCode === -1 ? '2px solid #1976d2' : '1.5px dashed #bbb',
                borderRadius: 2,
                px: 1.5,
                py: 1,
                minWidth: 70,
                alignSelf: 'stretch',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: activePathCode === -1 ? '#e3f2fd' : 'transparent',
                cursor: 'pointer',
                gap: 0.25,
              }}
            >
              <AddIcon sx={{ fontSize: 28, color: 'primary.main' }} />
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>
                הוסף מסלול
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <Box sx={{ bgcolor: '#e8f0fe', borderRadius: 2, p: 1.5, mt: 1, mb: 2 }}>
        <Box display="flex" alignItems="center" gap={0.5} mb={1}>
          <FilterAltIcon fontSize="small" />
          <Typography variant="subtitle1" fontWeight="bold">
            סנן לפי חשיפה
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'stretch', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Box>
            <Typography variant="body2" color="primary" fontWeight="bold" gutterBottom>
              חשיפה למניות מבוקשת
            </Typography>
            <MiniDoubleSlider
              min={0}
              max={100}
              rangeMin={stockExposureRange.min}
              rangeMax={stockExposureRange.max}
              onChange={(min, max) => setStockExposureRange({ min, max })}
              step={1}
              width={200}
              currentValue={targetStockExposure}
              currentValueColor="#1976d2"
            />
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem', direction: 'ltr', textAlign: 'right', display: 'block' }}>
              {stockExposureRange.min.toFixed(1)}% - {stockExposureRange.max.toFixed(1)}%
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              חשיפה למט"ח
            </Typography>
            <MiniDoubleSlider
              min={0}
              max={100}
              rangeMin={currencyExposureRange.min}
              rangeMax={currencyExposureRange.max}
              onChange={(min, max) => setCurrencyExposureRange({ min, max })}
              step={10}
              width={200}
            />
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem', direction: 'ltr', textAlign: 'right', display: 'block' }}>
              {currencyExposureRange.min}% - {currencyExposureRange.max}%
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              חשיפה לחו"ל
            </Typography>
            <MiniDoubleSlider
              min={0}
              max={100}
              rangeMin={foreignExposureRange.min}
              rangeMax={foreignExposureRange.max}
              onChange={(min, max) => setForeignExposureRange({ min, max })}
              step={10}
              width={200}
            />
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem', direction: 'ltr', textAlign: 'right', display: 'block' }}>
              {foreignExposureRange.min}% - {foreignExposureRange.max}%
            </Typography>
          </Box>
          </Box>
          <Box
            onClick={() => setIncludeSectoral((v) => !v)}
            sx={{
              border: includeSectoral ? '2px solid #1976d2' : '1px solid #bbb',
              borderRadius: 2,
              px: 1,
              py: 1,
              cursor: 'pointer',
              bgcolor: includeSectoral ? '#e3f2fd' : '#fff',
              alignSelf: 'stretch',
              display: 'flex',
              alignItems: 'center',
              width: 80,
              textAlign: 'center',
            }}
          >
            <Typography variant="caption" fontWeight="bold" sx={{ lineHeight: 1.3 }}>כלול קרנות סקטוריאליות</Typography>
          </Box>
        </Box>
      </Box>

      {noFunds && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          לא נמצאו קרנות מתאימות בטווח הנבחר
        </Typography>
      )}

      {currentHandlerFunds.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            קרנות מתאימות - {formatHandlerCode(mimshak.handler_code)}
          </Typography>
          <Typography variant="caption" color="text.secondary" gutterBottom display="block">
            מסודר לפי התאמה לחשיפה המבוקשת
          </Typography>
          <FundTable
            key={`current-${currentHandlerFunds.length}-${includeSectoral}`}
            funds={currentHandlerFunds}
            selectedFundId={selectedFundId ?? undefined}
            highlightedFundId={highlightedFundId ?? undefined}
            onSelect={handleFundSelect}
            exposureRanges={{ stock: stockExposureRange, currency: currencyExposureRange, foreign: foreignExposureRange }}
          />
        </Box>
      )}

      <Box>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" mb={1}>
          קרנות מתאימות מיצרנים אחרים
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              דירוג ביצועים 6M (אחוזון)
            </Typography>
            <MiniDoubleSlider
              min={0}
              max={100}
              rangeMin={performanceRatingRange.min}
              rangeMax={performanceRatingRange.max}
              onChange={(min, max) => setPerformanceRatingRange({ min, max })}
              step={5}
              width={200}
              rangeColor="rgba(33, 150, 243, 0.3)"
              handleColor="#2196F3"
            />
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem', direction: 'ltr', textAlign: 'right', display: 'block' }}>
              {performanceRatingRange.min}% - {performanceRatingRange.max}%
            </Typography>
          </Box>
        </Box>

        {filteredOtherHandlerFunds.length > 0 ? (
          <>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              מסודר לפי קרבה לחשיפה המבוקשת
            </Typography>
            <FundTable
              key={`other-${filteredOtherHandlerFunds.length}-${includeSectoral}`}
              funds={filteredOtherHandlerFunds}
              selectedFundId={selectedFundId ?? undefined}
              highlightedFundId={highlightedFundId ?? undefined}
              onSelect={handleFundSelect}
              exposureRanges={{ stock: stockExposureRange, currency: currencyExposureRange, foreign: foreignExposureRange }}
            />
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            לא נמצאו קרנות מיצרנים אחרים בטווח הנבחר
          </Typography>
        )}
      </Box>

    </Paper>
  );
};

export default SuggestedPathSelection;
